import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", subject: "", message: "", hp_field: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async e => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please tell us your name");
    if (!form.phone.trim() && !form.email.trim()) return toast.error("Add a phone number or email so we can reply");
    if (!form.message.trim()) return toast.error("Please write a message");
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Convert empty optional strings to null so Pydantic doesn't validate them.
      if (!payload.email?.trim()) delete payload.email;
      if (!payload.phone?.trim()) delete payload.phone;
      await axios.post(`${API}/contact`, payload);
      toast.success("Thanks! We've got your message.");
      setForm({ full_name: "", phone: "", email: "", subject: "", message: "", hp_field: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? (detail[0]?.msg || "Please check the form.") : "Something went wrong. Please call us instead.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-wash" data-testid="contact-hero">
        <div className="container-x py-12">
          <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Contact</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold">Get in touch with iCrack Solutions</h1>
          <p className="mt-3 max-w-2xl text-slate-600">Walk in, call, WhatsApp, email, or send us a message — we'll get back to you fast.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid gap-8 lg:grid-cols-2">
          <div className="space-y-4" data-testid="contact-details">
            <div className="rounded-2xl border border-slate-200 p-6 bg-white">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5" style={{ color: "var(--icrack-magenta)" }} />
                <div>
                  <h3 className="font-semibold">Shop address</h3>
                  <p className="text-slate-700">{BUSINESS.address.line1}, {BUSINESS.address.city}, {BUSINESS.address.postcode}, {BUSINESS.address.country}</p>
                  <a href={BUSINESS.googleDirections} target="_blank" rel="noreferrer" className="mt-1 inline-block link-magenta text-sm font-semibold" data-testid="contact-directions-link">Get directions →</a>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 bg-white">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 mt-0.5" style={{ color: "var(--icrack-magenta)" }} />
                <div className="flex-1">
                  <h3 className="font-semibold">Phone</h3>
                  <ul className="mt-1 space-y-1">
                    {BUSINESS.phones.map(p => (
                      <li key={p.tel}><a href={`tel:${p.tel}`} className="text-slate-700 hover:text-slate-900" data-testid={`contact-call-${p.label.toLowerCase()}`}>{p.display}</a> <span className="text-slate-400 text-sm">({p.label})</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 bg-white">
              <div className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 mt-0.5 text-green-600" />
                <div>
                  <h3 className="font-semibold">WhatsApp</h3>
                  <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer" className="link-magenta text-sm font-semibold" data-testid="contact-whatsapp-button">Chat on WhatsApp →</a>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 bg-white">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 mt-0.5" style={{ color: "var(--icrack-magenta)" }} />
                <div>
                  <h3 className="font-semibold">Email</h3>
                  <a href={`mailto:${BUSINESS.email}`} className="text-slate-700 hover:text-slate-900" data-testid="contact-email-link">{BUSINESS.email}</a>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 bg-white">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 mt-0.5" style={{ color: "var(--icrack-magenta)" }} />
                <div>
                  <h3 className="font-semibold">Opening hours</h3>
                  <ul className="mt-1 text-sm text-slate-700 grid grid-cols-2 gap-x-6">
                    {BUSINESS.hours.map(h => (
                      <li key={h.day} className="flex justify-between"><span className="font-medium">{h.day}</span><span>{h.open === "Closed" ? "Closed" : `${h.open}–${h.close}`}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="aspect-video overflow-hidden rounded-2xl border border-slate-200" data-testid="contact-map-wrapper">
              <iframe title="iCrack Solutions Google Map" src={BUSINESS.googleMapsEmbed} width="100%" height="100%" style={{border:0}} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-google-map"/>
            </div>

            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="contact-form">
              <h2 className="font-display text-2xl font-bold">Send us a message</h2>
              <input type="text" tabIndex="-1" autoComplete="off" className="hidden" value={form.hp_field} onChange={(e)=>update("hp_field", e.target.value)} aria-hidden data-testid="contact-honeypot"/>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="c_name">Full name</Label>
                  <Input id="c_name" value={form.full_name} onChange={e=>update("full_name", e.target.value)} className="h-11 rounded-xl" data-testid="contact-full-name-input"/>
                </div>
                <div>
                  <Label htmlFor="c_phone">Phone</Label>
                  <Input id="c_phone" value={form.phone} onChange={e=>update("phone", e.target.value)} placeholder="07XXX XXX XXX" className="h-11 rounded-xl" data-testid="contact-phone-input"/>
                </div>
              </div>
              <div>
                <Label htmlFor="c_email">Email</Label>
                <Input id="c_email" type="email" value={form.email} onChange={e=>update("email", e.target.value)} className="h-11 rounded-xl" data-testid="contact-email-input"/>
              </div>
              <div>
                <Label htmlFor="c_subject">Subject</Label>
                <Input id="c_subject" value={form.subject} onChange={e=>update("subject", e.target.value)} placeholder="e.g. iPhone 12 screen quote" className="h-11 rounded-xl" data-testid="contact-subject-input"/>
              </div>
              <div>
                <Label htmlFor="c_msg">Message</Label>
                <Textarea id="c_msg" rows={5} value={form.message} onChange={e=>update("message", e.target.value)} className="rounded-xl" data-testid="contact-message-textarea"/>
              </div>
              <Button type="submit" className="rounded-xl btn-primary-magenta h-11" disabled={submitting} data-testid="contact-submit-button">
                {submitting ? "Sending…" : (<><Send className="h-4 w-4 mr-1"/>Send message</>)}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
